import { z } from "zod";

export const DESIGNATION_OPTIONS = [
  { label: "Manager", value: "Manager" },
  { label: "Staff", value: "Staff" },
  { label: "Cleaner", value: "Cleaner" },
  { label: "Helper", value: "Helper" },
];

export const GENDER_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { label: "Permanent", value: "permanent" },
  { label: "Part Time", value: "part-time" },
  { label: "Contract", value: "contract" },
  { label: "Temporary", value: "temporary" },
];

export const SHIFT_SCHEDULE_OPTIONS = [
  { label: "Day", value: "1" },
  { label: "Night", value: "2" },
];

export const SALARY_TYPE_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Weekly", value: "weekly" },
  { label: "Hourly", value: "hourly" },
];

export const CURRENCY_OPTIONS = [
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "USD", value: "USD" },
];

export const STAFF_DEFAULT_VALUES = {
  first_name: "",
  last_name: "",
  designation: "",
  role_id: "",
  joining_date: null,
  gender: "",
  email: "",
  phone: "",
  address: "",
  employment_type: "",
  shift_schedule_id: "1",
  salary_type: "",
  salary: "",
  bank_account_number: "",
  bank_branch_name: "",
  bank_name: "",
  currency: "",
  passcode: "",
  shifts: [],
  file_manager_id: undefined,
};

export const toMinutes = (timeStr) => {
  if (!timeStr) return -1;
  const str = timeStr.toString().trim();

  if (str.includes("T")) {
    const timePart = str.split("T")[1];
    const parts = timePart.split(":");
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return isNaN(h) || isNaN(m) ? -1 : h * 60 + m;
  }

  if (str.toUpperCase().includes("AM") || str.toUpperCase().includes("PM")) {
    const isPM = str.toUpperCase().includes("PM");
    const isAM = str.toUpperCase().includes("AM");
    const cleanStr = str.replace(/AM|PM/gi, "").trim();
    const parts = cleanStr.split(":");
    let h = parseInt(parts[0], 10);
    const m = parts[1] ? parseInt(parts[1], 10) : 0;
    if (isNaN(h)) return -1;
    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;
    return h * 60 + (isNaN(m) ? 0 : m);
  }

  const parts = str.split(":");
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    return isNaN(h) || isNaN(m) ? -1 : h * 60 + m;
  }

  return -1;
};

export const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const mins = toMinutes(timeStr);
  if (mins < 0) return timeStr;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const displayH = h % 12 === 0 ? 12 : h % 12;
  return `${displayH.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}${ampm}`;
};

export const getSelectedShiftsList = (allShifts, selectedIds) => {
  if (!selectedIds || selectedIds.length === 0 || !Array.isArray(allShifts))
    return [];
  const numIds = selectedIds.map(Number);
  const matched = [];

  allShifts.forEach((dayGroup) => {
    if (Array.isArray(dayGroup?.shifts)) {
      dayGroup.shifts.forEach((s) => {
        if (numIds.includes(Number(s.id))) {
          matched.push({
            id: s.id,
            day: dayGroup.day,
            start_time: s.start_time,
            end_time: s.end_time,
          });
        }
      });
    }
  });

  return matched;
};

export const resolveShiftIds = (rawShifts, availableShifts) => {
  if (!Array.isArray(rawShifts) || rawShifts.length === 0) return [];
  if (!Array.isArray(availableShifts) || availableShifts.length === 0) {
    return rawShifts
      .map((s) => (typeof s === "object" ? Number(s?.shift_id || s?.id) : Number(s)))
      .filter((id) => !isNaN(id) && id > 0);
  }

  const allSlots = [];
  availableShifts.forEach((dayGroup) => {
    const day = (dayGroup?.day || "").toLowerCase().trim();
    if (Array.isArray(dayGroup?.shifts)) {
      dayGroup.shifts.forEach((slot) => {
        allSlots.push({
          id: Number(slot.id),
          day: day,
          startMin: toMinutes(slot.start_time),
          endMin: toMinutes(slot.end_time),
        });
      });
    }
  });

  const matchedIds = new Set();

  const processItem = (item, parentDay = "") => {
    if (typeof item === "number" || typeof item === "string") {
      const num = Number(item);
      if (allSlots.some((s) => s.id === num)) {
        matchedIds.add(num);
      }
      return;
    }

    if (!item || typeof item !== "object") return;

    const currentDay = (item.day || parentDay || "").toLowerCase().trim();

    if (Array.isArray(item.time_slots) && item.time_slots.length > 0) {
      item.time_slots.forEach((ts) => processItem(ts, currentDay));
      return;
    }

    if (Array.isArray(item.shifts) && item.shifts.length > 0) {
      item.shifts.forEach((s) => processItem(s, currentDay));
      return;
    }

    const itemStartMin = toMinutes(item.start_time);
    const itemEndMin = toMinutes(item.end_time);

    if (itemStartMin >= 0 && itemEndMin >= 0) {
      const found = allSlots.find((s) => {
        const dayMatch = !currentDay || s.day === currentDay;
        const timeMatch = s.startMin === itemStartMin && s.endMin === itemEndMin;
        return dayMatch && timeMatch;
      });

      if (found) {
        matchedIds.add(found.id);
        return;
      }
    }

    const directShiftId = Number(item.shift_id);
    if (!isNaN(directShiftId) && allSlots.some((s) => s.id === directShiftId)) {
      matchedIds.add(directShiftId);
      return;
    }

    const directId = Number(item.id);
    if (!isNaN(directId) && allSlots.some((s) => s.id === directId)) {
      matchedIds.add(directId);
    }
  };

  rawShifts.forEach((item) => processItem(item));

  return Array.from(matchedIds);
};

const baseStaffFields = {
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  designation: z.string().min(1, "Designation is required"),
  role_id: z.preprocess(
    (val) => {
      if (val === undefined || val === null || val === false || val === "false" || val === "") return "";
      return String(val).trim();
    },
    z.string().min(1, "Role is required")
  ),
  joining_date: z.preprocess(
    (val) => (val ? new Date(val) : undefined),
    z.date({ required_error: "Joining date is required" })
  ),
  gender: z.string().min(1, "Gender is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().regex(/^[0-9]{11}$/, "Phone number must be exactly 11 digits"),
  address: z.string().min(1, "Address is required"),
  employment_type: z.string().min(1, "Employment type is required"),
  shift_schedule_id: z.string().min(1, "Shift schedule ID is required"),
  salary_type: z.string().min(1, "Salary type is required"),
  salary: z.preprocess(
    (val) => (typeof val === "number" ? val.toString() : val),
    z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, "Salary must be a valid number")
      .transform((val) => Number(val))
      .refine((val) => val > 0, { message: "Salary must be greater than 0" })
  ),
  bank_account_number: z
    .string()
    .regex(/^[0-9]{8,20}$/, "Bank account number must be 8-20 digits"),
  bank_branch_name: z.string().min(1, "Bank branch name is required"),
  bank_name: z.string().min(1, "Bank name is required"),
  currency: z.string().min(1, "Currency is required"),
  shifts: z.array(z.number()).min(1, "At least one shift is required"),
  file_manager_id: z
    .number()
    .min(1, "File manager ID is required")
    .nullable()
    .optional(),
};

export const addNewStaffSchema = z.object({
  ...baseStaffFields,
  passcode: z.string().regex(/^[0-9]{4,6}$/, "Passcode must be 4-6 digits"),
});

export const updateStaffSchema = z.object({
  ...baseStaffFields,
  passcode: z
    .string()
    .optional()
    .refine((val) => !val || /^[0-9]{4,6}$/.test(val), {
      message: "Passcode must be 4-6 digits",
    }),
});
